import React from 'react';
import { cn } from '@/lib/utils';
import type { ClientHealthMetrics } from '@/types/client';
import { ratingColorClass } from '@/components/clients/shared/stageConstants';

interface HealthRatingsStripProps {
  healthMetrics: ClientHealthMetrics | null;
}

const CATEGORIES: { key: keyof ClientHealthMetrics; label: string }[] = [
  { key: 'avg_gear_quality', label: 'Gear' },
  { key: 'avg_adequate_labor', label: 'Labor' },
  { key: 'avg_expectation_management', label: 'Expect.' },
  { key: 'avg_organization', label: 'Org.' },
  { key: 'avg_overall', label: 'Overall' },
];

function blockColor(blockIndex: number, value: number | null): string {
  if (value === null) return 'bg-surface-3/50';
  return blockIndex < Math.round(value) ? ratingColorClass(value) : 'bg-surface-3/50';
}

export const HealthRatingsStrip: React.FC<HealthRatingsStripProps> = ({ healthMetrics }) => {
  const hasRatings = healthMetrics && healthMetrics.rating_count > 0;

  return (
    <div className="bg-surface-1 border border-border rounded p-4">
      <h2 className="font-heading text-sm uppercase tracking-wider text-muted-foreground mb-3">
        Health Ratings
      </h2>
      {!hasRatings ? (
        <p className="text-sm text-muted-foreground">No ratings yet. Rate bookings to build health data.</p>
      ) : (
        <div className="flex gap-3 md:gap-4 overflow-x-auto pb-1 scrollbar-none">
          {CATEGORIES.map(({ key, label }) => {
            const value = healthMetrics[key] as number | null;
            return (
              <div
                key={key}
                role="meter"
                aria-label={label}
                aria-valuenow={value ?? undefined}
                aria-valuemin={1}
                aria-valuemax={5}
                className="flex flex-col items-center gap-1.5 min-w-[52px]"
              >
                <span className="text-[10px] font-label uppercase tracking-wider text-muted-foreground">
                  {label}
                </span>
                <div className="flex gap-0.5">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <div
                      key={i}
                      className={cn(
                        'w-2.5 h-5 rounded-sm transition-colors',
                        blockColor(i, value)
                      )}
                    />
                  ))}
                </div>
                <span className="font-mono text-xs text-foreground">
                  {value !== null ? value.toFixed(1) : '--'}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
